import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Trash2, Skull, Shield, Coins, Menu, X } from "lucide-react";
import JoinDiscord from "@/components/JoinDiscord";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AddProfileDialog from "@/components/AddProfileDialog";
import SkillsPanel from "@/components/SkillsPanel";
import SlayerMaster from "@/components/SlayerMaster";
import GoalRoadmap from "@/components/GoalRoadmap";
import HistoryChart from "@/components/HistoryChart";
import ActivityTickerBar from "@/components/ActivityTickerBar";
import Leaderboard from "@/components/Leaderboard";
import Rewards from "@/components/Rewards";
import Champions from "@/components/Champions";
import Friends from "@/components/Friends";
import BondPot from "@/components/BondPot";
import InviteFriend from "@/components/InviteFriend";
import DailyRewards from "@/components/DailyRewards";
import ShareRank from "@/components/ShareRank";
import WeeklyRecap from "@/components/WeeklyRecap";
import AdminDashboard from "@/components/AdminDashboard";
import { Logo } from "@/components/Logo";
import AdBanner from "@/components/AdBanner";
import SiteFooter from "@/components/SiteFooter";
import SyncOnboarding from "@/components/SyncOnboarding";
import OwnershipChallenge from "@/components/OwnershipChallenge";
import { GamemodeDropdown } from "@/components/GamemodeDropdown";
import DiscordWelcome from "@/components/DiscordWelcome";
import OnlineNow from "@/components/OnlineNow";
import { MODE_LABEL, modeColor } from "@/lib/gamemode";


export default function Dashboard() {
  const { user, logout } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [stats, setStats] = useState({ total_points: 0, completed: 0, verified: 0, streak: 0 });
  const [taskReload, setTaskReload] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [onlineNow, setOnlineNow] = useState(0);
  const [profilesReady, setProfilesReady] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try { const { data } = await api.get("/public/online"); if (active) setOnlineNow(data.online_now || 0); } catch {}
    };
    const ping = () => api.post("/presence/ping").catch(() => {});
    ping(); load();
    const t = setInterval(() => { ping(); load(); }, 10000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const announceAutoCompleted = (auto) => {
    if (!auto || !auto.length) return;
    const pts = auto.reduce((s, t) => s + (t.points || 0), 0);
    toast.success(
      `✅ ${auto.length} task${auto.length > 1 ? "s" : ""} auto-completed! +${pts} pts — ${auto.slice(0, 2).map((t) => t.title).join(", ")}${auto.length > 2 ? "…" : ""}`,
      { duration: 9000 }
    );
    loadStats(selectedId);
    setTaskReload((n) => n + 1);
  };

  const loadStats = async (pid = selectedId) => {
    if (!pid) {
      setStats({ total_points: 0, completed: 0, verified: 0, streak: 0 });
      return;
    }
    try {
      const { data } = await api.get(`/profiles/${pid}/stats`);
      setStats(data);
    } catch (e) {
      /* non-critical */
    }
  };

  const loadProfiles = async () => {
    const { data } = await api.get("/profiles");
    setProfiles(data);
    if (data.length && !selectedId) setSelectedId(data[0].id);
    return data;
  };

  useEffect(() => {
    loadProfiles()
      .then(() => setProfilesReady(true))
      .catch((e) => { setProfilesReady(true); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
    // Nudge season champions with an unclaimed Bond to claim it.
    api
      .get("/seasons/champions")
      .then(({ data }) => {
        const win = (data || []).find((r) => r.is_me && r.bond_status === "pending" && !r.claim);
        if (win) {
          toast.success(`👑 You're the ${win.season} champion! Open Champions to claim your free OSRS Bond.`, {
            duration: 15000,
          });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setProfile(null);
      setStats({ total_points: 0, completed: 0, verified: 0, streak: 0 });
      return;
    }
    loadStats(selectedId);
    setLoadingProfile(true);
    api
      .get(`/profiles/${selectedId}`)
      .then(({ data }) => setProfile(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoadingProfile(false));
  }, [selectedId]);

  const onProfileAdded = (p) => {
    setProfiles((prev) => [...prev, p]);
    setSelectedId(p.id);
  };

  const [syncing, setSyncing] = useState(false);
  const syncExternal = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/profiles/${selectedId}/sync-external`);
      setProfile(data);
      const synced = data.external?.wikisync?.synced;
      toast.success(synced ? "WikiSync data synced! Your quest & diary tasks are now verifiable." : "Synced. WikiSync has no data — install the plugin & sync in-game.");
      if (data.auto_completed?.length) announceAutoCompleted(data.auto_completed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [changingMode, setChangingMode] = useState(false);
  const changeGamemode = async (gm) => {
    if (!gm || !profile || gm === profile.gamemode) return;
    setChangingMode(true);
    try {
      const { data } = await api.patch(`/profiles/${profile.id}/gamemode`, { gamemode: gm });
      setProfile(data);
      setProfiles((prev) => prev.map((p) => (p.id === data.id ? { ...p, gamemode: gm } : p)));
      toast.success(`Account type changed to ${MODE_LABEL[gm]}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setChangingMode(false);
    }
  };
  const [togglingF2p, setTogglingF2p] = useState(false);
  const toggleF2p = async () => {
    if (!profile || togglingF2p) return;
    const next = !profile.f2p_only;
    setTogglingF2p(true);
    try {
      const { data } = await api.patch(`/profiles/${profile.id}/f2p`, { f2p_only: next });
      setProfile(data.profile);
      setProfiles((prev) => prev.map((p) => (p.id === data.profile.id ? { ...p, f2p_only: next } : p)));
      toast.success(next ? "F2P mode on — applies to your next task." : "Members mode on — applies to your next task.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTogglingF2p(false);
    }
  };
  const deleteProfile = async () => {
    setConfirmDelete(false);
    try {
      await api.delete(`/profiles/${selectedId}`);
      const remaining = profiles.filter((p) => p.id !== selectedId);
      setProfiles(remaining);
      setSelectedId(remaining[0]?.id || null);
      toast.success("Account removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const snap = profile?.snapshot;

  return (
    <div className="stone-bg App pb-16">
      {/* Header */}
      <header className="osrs-bar border-b-4 border-b-[#0b0906] shadow-[inset_0_-3px_0_#7d6a44] px-4 sm:px-6 py-3 flex items-center justify-between gap-3 relative z-30" data-testid="dashboard-header">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Logo variant="header" />
          <OnlineNow count={onlineNow} />
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="sm:hidden inline-flex items-center gap-1.5 osrs-panel-plain shrink-0 whitespace-nowrap px-3 py-1.5 osrs-num text-[#FFCF3F] osrs-shadow text-base" data-testid="total-points-mobile" title="Points earned this month — resets on the 1st. Lifetime totals live on the all-time leaderboard.">
            <Coins size={16} className="text-[#FFCF3F] shrink-0" /> {stats.total_points}
          </span>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            onClick={() => setMenuOpen((o) => !o)}
            className="osrs-btn px-3 py-2 min-h-[44px] sm:hidden flex items-center justify-center"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div
            data-testid="header-actions"
            className={`${menuOpen ? "flex" : "hidden"} sm:flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 absolute sm:static right-4 top-full sm:top-auto mt-1 sm:mt-0 p-3 sm:p-0 w-60 sm:w-auto osrs-panel sm:!bg-transparent sm:!bg-none sm:!border-0 sm:!shadow-none z-30`}
          >
            {user?.role === "admin" && <AdminDashboard />}
            <DailyRewards profileId={selectedId} ready={profilesReady} onReward={() => loadStats(selectedId)} />
            <Champions />
            <Friends />
            <InviteFriend />
            <Rewards profileId={selectedId} refreshKey={`${stats.total_points}-${taskReload}`} />
            <Leaderboard />
            <BondPot />
            <span className="hidden sm:inline-flex items-center gap-1.5 osrs-panel-plain shrink-0 whitespace-nowrap px-4 py-1.5 osrs-num text-[#FFCF3F] osrs-shadow text-lg" data-testid="total-points" title="Points earned this month — resets on the 1st. Lifetime totals live on the all-time leaderboard.">
              <Coins size={16} className="text-[#FFCF3F] shrink-0" /> {stats.total_points} pts
            </span>
            <span className="osrs-shadow text-sm text-[#E6A519] px-1 order-first sm:order-none truncate" data-testid="user-name">{user?.name}</span>
            <button data-testid="logout-btn" onClick={logout} className="osrs-btn osrs-btn-red px-3 py-2 min-h-[44px] sm:min-h-0 text-sm flex items-center justify-center gap-2">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <ActivityTickerBar />
        {/* Profile selector row */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          {profiles.map((p) => (
            <button
              key={p.id}
              data-testid={`profile-tab-${p.id}`}
              onClick={() => setSelectedId(p.id)}
              className={`osrs-btn px-4 py-2 text-sm flex items-center gap-2 ${selectedId === p.id ? "shadow-[inset_0_0_0_2px_#FFCF3F]" : "opacity-55"}`}
            >
              <Skull size={14} /> {p.username}
              <span className="osrs-num text-xs" style={{ color: modeColor(p.gamemode) }}>{MODE_LABEL[p.gamemode]}</span>
              {p.ownership_verified === false && (
                <span className="osrs-num text-[10px] text-[#FF9966] uppercase" title="Ownership not verified" data-testid={`tab-unverified-${p.id}`}>
                  ⚠ verify
                </span>
              )}
            </button>
          ))}
          <AddProfileDialog onAdded={onProfileAdded} />
        </div>

        {profiles.length === 0 ? (
          <div className="osrs-panel p-10 text-center max-w-xl mx-auto" data-testid="empty-state">
            <Shield size={56} className="mx-auto text-[#5a4a2a] mb-4" />
            <h2 className="osrs-title text-2xl mb-2">No accounts tracked yet</h2>
            <p className="osrs-shadow text-[#E6A519] mb-6">
              Add your Old School RuneScape username to pull your live stats and get personalised tasks.
            </p>
            <div className="inline-block"><AddProfileDialog onAdded={onProfileAdded} /></div>
          </div>
        ) : loadingProfile || !profile ? (
          <p className="osrs-title text-xl osrs-shadow" data-testid="loading-profile">Reading the hiscores...</p>
        ) : (
          <>
            {/* Account summary bar */}
            <div className="osrs-panel p-4 mb-6 flex items-center justify-between flex-wrap gap-4 ts-rise" data-testid="account-summary">
              <div>
                <h2 className="osrs-title osrs-text-glow text-3xl" data-testid="profile-username">{profile.username}</h2>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <GamemodeDropdown value={profile.gamemode} onChange={changeGamemode} changing={changingMode} />
                  <button
                    data-testid="f2p-toggle"
                    onClick={toggleF2p}
                    disabled={togglingF2p}
                    role="switch"
                    aria-checked={!!profile.f2p_only}
                    title={profile.f2p_only ? "Free-to-play tasks only. Click to switch to all (members) tasks." : "All tasks. Click to switch to free-to-play only."}
                    className="relative flex items-center gap-1 rounded-full p-0.5 h-6 select-none disabled:opacity-60 transition-[filter] hover:brightness-110 border border-[#0f0d09] bg-[#1a1712]"
                    style={{ boxShadow: "inset 0 1px 3px rgba(0,0,0,0.6)" }}
                  >
                    {/* sliding knob */}
                    <span
                      className={`absolute top-0.5 bottom-0.5 rounded-full transition-all duration-200 ease-out ${profile.f2p_only ? "left-0.5 right-[calc(50%-0.125rem)] bg-[#3a7d1e]" : "left-[calc(50%-0.125rem)] right-0.5 bg-[#8a6d3b]"}`}
                      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.5)" }}
                    />
                    <span className={`relative z-10 px-1.5 text-[9px] font-bold uppercase tracking-wide transition-colors ${profile.f2p_only ? "text-white osrs-shadow" : "text-[#8a7a54]"}`}>F2P</span>
                    <span className={`relative z-10 px-1.5 text-[9px] font-bold uppercase tracking-wide transition-colors ${!profile.f2p_only ? "text-white osrs-shadow" : "text-[#8a7a54]"}`}>All</span>
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="osrs-shadow text-xs text-[#E6A519] uppercase">Combat</p>
                  <p className="osrs-num text-[#FFCF3F] osrs-shadow text-3xl" data-testid="stat-combat-level">{snap?.combat_level}</p>
                </div>
                <div className="text-center">
                  <p className="osrs-shadow text-xs text-[#E6A519] uppercase">Total Lvl</p>
                  <p className="osrs-num text-[#00FF00] osrs-shadow text-3xl">{snap?.total_level}</p>
                </div>
                <div className="text-center hidden sm:block">
                  <p className="osrs-shadow text-xs text-[#E6A519] uppercase">Total XP</p>
                  <p className="osrs-num text-[#FFCF3F] osrs-shadow text-2xl">{Number(snap?.total_xp || 0).toLocaleString()}</p>
                </div>
                <div className="flex flex-col gap-1">
                  {profile.ownership_verified !== false && <ShareRank profile={profile} />}
                  {profile.ownership_verified !== false && <WeeklyRecap profile={profile} />}
                  <button data-testid="delete-profile-btn" onClick={() => setConfirmDelete(true)} className="osrs-btn osrs-btn-red px-3 py-2 text-xs flex items-center gap-2">
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              </div>
            </div>

            {profile.ownership_verified === false ? (
              <OwnershipChallenge
                profile={profile}
                onVerified={(updated) => {
                  const merged = updated || { ...profile, ownership_verified: true };
                  setProfile(merged);
                  setProfiles((prev) => prev.map((p) => (p.id === merged.id ? { ...p, ownership_verified: true } : p)));
                  loadStats(profile.id);
                }}
              />
            ) : (
              <>
                {/* Task verification (single consolidated panel) */}
                <SyncOnboarding profile={profile} onSync={syncExternal} syncing={syncing} />

                {/* Bento layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ts-rise">
                  <div className="lg:col-span-1 space-y-6">
                    <SkillsPanel snapshot={snap} />
                    <HistoryChart profileId={profile.id} refreshKey={profile.updated_at} />
                  </div>
                  <div className="lg:col-span-2">
                    <SlayerMaster profile={profile} onPointsChange={loadStats} totalPoints={stats.total_points} reloadKey={taskReload} />
                    {profile.ownership_verified !== false && <GoalRoadmap profile={profile} />}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>

      {confirmDelete && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setConfirmDelete(false)}>
          <div className="osrs-panel p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete-dialog">
            <h3 className="osrs-title text-xl mb-3">Remove account?</h3>
            <p className="osrs-shadow text-[#E6A519] text-sm mb-6 leading-snug">
              Stop tracking <span className="text-[#FFCF3F] font-bold">{profile.username}</span>? This deletes its tasks, points and history and can't be undone.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button data-testid="cancel-delete-btn" onClick={() => setConfirmDelete(false)} className="osrs-btn px-4 py-2 text-sm">
                Cancel
              </button>
              <button data-testid="confirm-delete-btn" onClick={deleteProfile} className="osrs-btn osrs-btn-red px-4 py-2 text-sm flex items-center gap-2">
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <SiteFooter />
      <JoinDiscord variant="fab" />
      <DiscordWelcome />
      <AdBanner />
    </div>
  );
}
